/**
 * 项目顶部市检入口：展示本周已确认的检查，并允许有项目管理权限的成员直接添加市检。
 * 复用工程周历的真实预约与项目权限；洛杉矶时间周一至周日为一周，待预约、旧日期和取消项不计入。
 * 顶部只展示首项摘要和总数，多项通过浮层完整查看；有权限时即使本周为空也保留新增入口。
 */
import React, { useCallback, useEffect, useState } from "react";
import { CalendarCheck, ChevronDown, Plus, RefreshCw } from "lucide-react";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import ScheduleInspectionDetail from "../subcontract/ScheduleInspectionDetail";
import ScheduleInspectionForm from "../subcontract/ScheduleInspectionForm";
import { inspectionPeriodLabel } from "../subcontract/ScheduleInspectionCard";
import { inspectionToday, inspectionWeekStart, shiftInspectionDay } from "../../lib/inspectionWeek";

/** 预约日期是纯日历日；按 UTC 格式化以避免在美国电脑上被显示成前一天。 */
const dateLabel = (day, zh) => new Intl.DateTimeFormat(zh ? "zh-CN" : "en-US", {
  timeZone: "UTC", month: "numeric", day: "numeric", weekday: "short",
}).format(new Date(`${day}T12:00:00Z`));
const attendeesLabel = (row, zh) => (row?.attendees || []).map((person) => person.name).filter(Boolean).join(zh ? "、" : ", ") || (zh ? "待安排" : "To be assigned");

/** api 为现有登录客户端，projectId 限定当前项目，zh 控制双语；无预约且无管理权限时不占页头空间。 */
export default function WeeklyInspectionSummary({ api, projectId, zh }) {
  const [today, setToday] = useState(inspectionToday);
  const [revision, setRevision] = useState(0);
  const [result, setResult] = useState(null);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState("");
  const [addDay, setAddDay] = useState("");
  const start = inspectionWeekStart(today);
  const end = shiftInspectionDay(start, 7);
  const scope = `${projectId}:${start}`;
  const refresh = useCallback(() => {
    setToday(inspectionToday());
    setRevision((value) => value + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setResult({ scope, loading: true, events: [] });
    // 周历接口已经核验服务端权限；只取本项目已确认项，不拿希望检查日期或旧项目字段推算预约。
    api.get("/inspection-schedule/week", { params: { start }, signal: controller.signal, timeout: 20000 })
      .then(({ data }) => {
        if (controller.signal.aborted) return;
        const events = (data.events || []).filter((row) => row.projectId === projectId && row.status === "scheduled" && row.confirmedDate >= start && row.confirmedDate < end)
          .sort((a, b) => a.confirmedDate.localeCompare(b.confirmedDate) || (a.confirmedPeriod || "").localeCompare(b.confirmedPeriod || "") || (a.title || "").localeCompare(b.title || ""));
        const project = (data.projects || []).find((row) => row.id === projectId);
        setResult({ scope, loading: false, events, canManage: project?.canManage === true, address: project?.address || "" });
      })
      .catch(() => {
        // 失败不能伪装成“本周没有检查”；清除旧摘要并提供重试，避免成员依赖已经改期的旧信息。
        if (!controller.signal.aborted) setResult({ scope, loading: false, events: [], error: true });
      });
    return () => controller.abort();
  }, [api, projectId, start, end, scope, revision]);

  useEffect(() => {
    // 日程编辑后即时刷新，回到窗口再核对；持续打开跨周时也自动切换到新周，不保留上周提醒。
    window.addEventListener("sub-schedule-changed", refresh);
    window.addEventListener("focus", refresh);
    const timer = window.setInterval(() => setToday(inspectionToday()), 60000);
    return () => {
      window.removeEventListener("sub-schedule-changed", refresh);
      window.removeEventListener("focus", refresh);
      window.clearInterval(timer);
    };
  }, [refresh]);
  useEffect(() => { setOpen(false); setDetail(""); setAddDay(""); }, [scope]);

  // 切换项目的首帧即隔离旧请求结果，迟到响应也不能把另一项目的检查带进当前页头。
  const current = result?.scope === scope ? result : null;
  // 摘要刷新与详情弹窗独立：成员切回窗口或日程变更时，不应卸载正在编辑的详情并丢失输入。
  const saved = () => { setAddDay(""); setOpen(false); refresh(); };
  return <><InspectionSummaryContent current={current} zh={zh} refresh={refresh} open={open} setOpen={setOpen} start={start} end={end} onDetail={setDetail} onAdd={() => setAddDay(today >= start && today < end ? today : start)} />
    {addDay && <ScheduleInspectionForm day={addDay} projectId={projectId} projects={[{ id: projectId, address: current?.address || "" }]} initialStatus="scheduled" api={api} zh={zh} onClose={() => setAddDay("")} onSaved={saved} />}
    {detail && <ScheduleInspectionDetail id={detail} api={api} zh={zh} onClose={() => setDetail("")} onUpdated={refresh} />}
  </>;
}

/** 负责摘要、检查列表及新增入口；详情生命周期由上层保持，加载、空态与重试不会影响已打开的详情。 */
function InspectionSummaryContent({ current, zh, refresh, open, setOpen, start, end, onDetail, onAdd }) {
  if (!current || current.loading) return <span role="status" className="inline-flex items-center gap-1.5 text-xs text-slate-500" data-testid="hub-weekly-inspection-loading"><CalendarCheck aria-hidden="true" className="h-3.5 w-3.5" />{zh ? "市检加载中…" : "Loading inspections…"}</span>;
  if (current.error) return <Button type="button" size="sm" variant="outline" onClick={refresh} className="h-auto min-h-8 max-w-full whitespace-normal text-xs" data-testid="hub-weekly-inspection-retry"><RefreshCw aria-hidden="true" />{zh ? "市检加载失败 · 重试" : "Inspections unavailable · Retry"}</Button>;
  if (!current.events.length && !current.canManage) return null;

  const first = current.events[0];
  const heading = zh ? `本周市政府检查 · ${current.events.length} 项` : `City inspections this week · ${current.events.length}`;
  const summary = first ? `${dateLabel(first.confirmedDate, zh)} ${inspectionPeriodLabel(first.confirmedPeriod, zh)} · ${first.title}` : (zh ? "本周暂无已安排的检查" : "No inspections scheduled this week");
  return <div className="min-w-0 max-w-full md:max-w-sm" data-testid="hub-weekly-inspection-summary">
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild><Button type="button" variant="outline" aria-label={`${heading} · ${summary} · ${zh ? "现场：" : "On-site: "}${attendeesLabel(first, zh)}`} className="h-auto min-h-11 w-full justify-start gap-2 border-brand-200 bg-brand-50/50 px-2.5 py-1.5 text-left text-brand-600 shadow-none">
        <CalendarCheck aria-hidden="true" />
        <span className="min-w-0 flex-1 text-xs leading-5">
          <span className="block truncate font-semibold">{heading}</span>
          <span className="block truncate text-slate-700">{summary}</span>
          <span className="block truncate font-normal text-slate-500">{zh ? "现场：" : "On-site: "}{attendeesLabel(first, zh)}</span>
        </span>
        <ChevronDown aria-hidden="true" />
      </Button></PopoverTrigger>
      <PopoverContent align="start" className="w-96 max-w-[calc(100vw-2rem)] p-3" aria-label={heading} data-testid="hub-weekly-inspection-list">
        <h2 className="text-sm font-semibold text-slate-900">{heading}</h2>
        <p className="mt-1 text-xs text-slate-500">{start} — {shiftInspectionDay(end, -1)}</p>
        {current.events.length > 0 ? <ul className="mt-3 max-h-[50dvh] space-y-2 overflow-y-auto">
          {current.events.map((row) => <li key={row.id} className="rounded-lg border border-brand-100 bg-brand-50/40 p-2.5 text-xs" data-testid={`hub-weekly-inspection-${row.id}`}>
            <p className="font-semibold text-brand-600">{dateLabel(row.confirmedDate, zh)} · {inspectionPeriodLabel(row.confirmedPeriod, zh)}</p>
            <p className="mt-1 break-words text-sm font-medium text-slate-900 [overflow-wrap:anywhere]">{row.title}</p>
            <p className="mt-1 break-words text-slate-600 [overflow-wrap:anywhere]">{zh ? "现场：" : "On-site: "}{attendeesLabel(row, zh)}</p>
            {/* 项目成员可读摘要；只有接口明确授予详情权限时才提供详情入口，不扩张现有权限。 */}
            {row.canOpen && <Button type="button" size="sm" variant="ghost" className="mt-1 h-8 px-2 text-brand-600" onClick={() => { setOpen(false); onDetail(row.id); }}>{zh ? "查看详情" : "View details"}</Button>}
          </li>)}
        </ul> : <p className="mt-3 rounded-lg bg-slate-50 px-3 py-4 text-center text-sm text-slate-500">{zh ? "本周还没有已安排的检查" : "No inspections are scheduled this week."}</p>}
        {current.canManage && <Button type="button" size="sm" className="mt-3 w-full" data-testid="hub-add-city-inspection" onClick={() => { setOpen(false); onAdd(); }}><Plus aria-hidden="true" />{zh ? "添加市政检查" : "Add city inspection"}</Button>}
      </PopoverContent>
    </Popover>
  </div>;
}
